import React from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ClaimContextProvider, useClaim } from '~/components/ClaimContainer';
import WithConfirm from '~/components/ConfirmModal';
import ContactDetails from '~/components/Contacts/ContactMiniCard/Details';
import IdentityHeader from '~/components/Contacts/ContactMiniCard/IdentityHeader';
import IdentitySubHeader from '~/components/Contacts/ContactMiniCard/IdentitySubHeader';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { CloseIcon_Deprecated, PencilIcon } from '~/components/icons';
import HistoryIcon from '~/components/icons/HistoryIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import useOrganization from '~/components/OrganizationContext';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

import PermissionsButtonWrapper from '../../core/Permissions/PermissionsButtonWrapper';

interface ClaimWrapperProps {
  claimIdToFetch?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  claim: any;
  reloadClaim: () => Promise<void>;
}
const ClaimWrapper: React.FC<React.PropsWithChildren<ClaimWrapperProps>> = ({
  children,
  claimIdToFetch,
  claim,
  reloadClaim,
}) => {
  return claimIdToFetch ? (
    <ClaimContextProvider claim={claim} refreshData={reloadClaim} createMinimizedContext={false}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {children}
    </ClaimContextProvider>
  ) : (
    <>{children}</>
  );
};

interface ContactMiniCardPropsType {
  contact: ContactFullModel;
  claimId?: number;
  onEdit: () => void;
  onClose?: () => void;
  onUpdate: () => Promise<void>;
}

const ContactMiniCard: React.FC<ContactMiniCardPropsType> = ({ contact, onEdit, onClose, onUpdate, claimId }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { claim: contextClaim } = useClaim();
  const { user, userOrganization } = useCms();

  const handleReactivate = async () => {
    try {
      await axios.post(`/api/v1/organizations/${userOrganization.id}/contacts/${contact.id}/reactivate`);
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  let claimIdToFetch = claimId;
  if (!claimId && contextClaim) {
    claimIdToFetch = contextClaim.id;
  }
  const [claim, isLoading, isError, reloadClaim] = useFetchClaim(claimIdToFetch);
  const reloadClaimAsync = () => Promise.resolve(reloadClaim());

  const contactRole = organizationContactRolesDict[contact.role];
  const isSystemContact = !!contactRole.is_system;
  const hasContactWritePermission = useHasPermission({
    action: PERMISSION_ACTIONS.CONTACT,
    verb: PERMISSION_VERBS.WRITE,
  });

  if (isLoading) {
    return <LoadingIndicator isError={isError} />;
  }

  return (
    <ClaimWrapper claimIdToFetch={claimIdToFetch} claim={claim} reloadClaim={reloadClaimAsync}>
      <CardDialog
        title={<IdentityHeader contact={contact} />}
        subheader={<IdentitySubHeader contact={contact} />}
        width="470px"
        action={
          <div className="self-start">
            {hasContactWritePermission && !isSystemContact && !contact.is_deactivated ? (
              <PermissionsButtonWrapper>
                <IconButton onClick={onEdit} disabled={user.role.is_view_only}>
                  <PencilIcon />
                </IconButton>
              </PermissionsButtonWrapper>
            ) : null}
            {hasContactWritePermission && !isSystemContact && contact.is_deactivated ? (
              <PermissionsButtonWrapper action={PERMISSION_ACTIONS.DEACTIVATE_CONTACT} verb={PERMISSION_VERBS.WRITE}>
                {/* Tooltip attaches a ref and its hover/focus handlers to its child, so that child has to be a
                    DOM element. WithConfirm is a class component that forwards neither. */}
                <Tooltip title="Reactivate contact">
                  <span>
                    <WithConfirm
                      title="Reactivate Contact"
                      contentText={`Are you sure you want to reactivate "${contact.full_name}"? This will mark the contact as active again.`}
                      primaryButtonName="Reactivate Contact"
                      shouldCloseOnPrimary
                    >
                      <IconButton
                        aria-label="Reactivate contact"
                        onClick={handleReactivate}
                        disabled={user.role.is_view_only}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </WithConfirm>
                  </span>
                </Tooltip>
              </PermissionsButtonWrapper>
            ) : null}
            {onClose ? (
              <IconButton onClick={onClose}>
                <CloseIcon_Deprecated />
              </IconButton>
            ) : null}
          </div>
        }
      >
        <>
          <ContactDetails contact={contact} onUpdate={onUpdate} />
        </>
      </CardDialog>
    </ClaimWrapper>
  );
};

export default ContactMiniCard;
